<template>
 <section>
  <div class="vehicle-dashboard">
   <div class="vehicle-dashboard-container">
    <v-card class="filter-block">
     <Filters :placeHolder="'Search Vehicle Number'" @get-filter="getFilters"></Filters>
    </v-card>

    <v-card class="list-block">
     <div class="list-header">
      <div class="count-block">
       <span class="count-chip count-chip--total">Total Vehicles - <b>{{ totalCount }}</b></span>
       <span class="count-chip count-chip--active">Active - <b>{{ active }}</b></span>
       <span class="count-chip count-chip--inactive">Inactive - <b>{{ deactive }}</b></span>
      </div>
      <v-btn class="add-btn" color="#1f5c55" @click="openDialog({}, 'insert')">Add Vehicle <i class="fas fa-plus-circle"></i></v-btn>
     </div>

     <v-simple-table fixed-header height="calc(100vh - 345px)" class="vehicle-table">
      <thead>
       <tr>
        <th v-for="column in columns" :key="column.key" :class="{ sortable: column.sortable }" @click="column.sortable && toggleSort(column.key)">
         <div class="th-content">
          <span>{{ column.label }}</span>
          <v-icon v-if="column.sortable" small class="sort-icon" :class="{ active: sortKey === column.key }">{{ sortIcon(column.key) }}</v-icon>
         </div>
        </th>
       </tr>
      </thead>
      <tbody>
       <tr v-if="!sortedList.length">
        <td :colspan="columns.length" class="empty-row">No vehicles found</td>
       </tr>
       <tr :class="{ deleted: vehicle.trashStatus === 1 }" v-for="(vehicle, index) in sortedList" :key="vehicle.token || index">
        <td>
         <div class="vehicle-details">
          <v-tooltip bottom>
           <template v-slot:activator="{ on, attrs }">
            <span class="active-status" :class="{ inactive: vehicle.trash }" v-on="on" v-bind="attrs"></span>
           </template>
           <span>{{ vehicle.trash ? "inactive" : "active" }}</span>
          </v-tooltip>
          <div class="details">
           <span class="vehicle-no">{{ vehicle.vehicleNo }}</span>
           <span class="model-line">
            <span v-if="vehicle.category" class="category-badge" :class="categoryClass(vehicle.category)">{{ vehicle.category }}</span>
            <span class="model-name">{{ vehicle.manufacturer }} {{ vehicle.model }}</span>
           </span>
          </div>
         </div>
        </td>

        <td class="driver-column">
         <v-tooltip bottom :disabled="!vehicle.drivers.length">
          <template v-slot:activator="{ on, attrs }">
           <div class="driver-text" v-bind="attrs" v-on="on">{{ driverNames(vehicle) || "---" }}</div>
          </template>
          <span>{{ driverNames(vehicle) }}</span>
         </v-tooltip>
        </td>

        <td class="document-column" v-for="column in validityColumns" :key="column.key">
         <span v-if="vehicle.document && vehicle.document[column.doc] && vehicle.document[column.doc].expdate">
          {{ getExpiry(vehicle.document[column.doc].expdate) }}
          <i class="fas fa-exclamation-triangle" v-if="checkExpiry(vehicle.document[column.doc].verified)" :class="{ 'expiry-soon': vehicle.document[column.doc].verified === 3 }"></i>
         </span>
         <span v-else>---</span>
        </td>

        <td>
         <button type="button" class="documents-btn" @click="openDialog(vehicle, 'document')"><i class="far fa-file-alt"></i> Documents</button>
        </td>

        <td class="action-column">
         <div class="action-buttons" v-if="vehicle.trashStatus === 0">
          <v-tooltip bottom>
           <template v-slot:activator="{ on, attrs }">
            <button type="button" class="icon-btn icon-btn--edit" @click="openDialog(vehicle, 'edit')" v-on="on" v-bind="attrs"><i class="fas fa-pencil-alt"></i></button>
           </template>
           <span>Edit</span>
          </v-tooltip>
          <v-tooltip bottom>
           <template v-slot:activator="{ on, attrs }">
            <button type="button" class="icon-btn icon-btn--status" @click="openDialog(vehicle, 'statusChange')" v-on="on" v-bind="attrs">
             <i class="fas" :class="vehicle.trash ? 'fa-undo' : 'fa-ban'"></i>
            </button>
           </template>
           <span>{{ vehicle.trash ? "Activate" : "Deactivate" }}</span>
          </v-tooltip>
          <v-tooltip bottom>
           <template v-slot:activator="{ on, attrs }">
            <button type="button" class="icon-btn icon-btn--info" @click="openDialog(vehicle, 'remarks')" v-on="on" v-bind="attrs"><i class="fas fa-info-circle"></i></button>
           </template>
           <span>Remarks</span>
          </v-tooltip>
          <v-tooltip bottom>
           <template v-slot:activator="{ on, attrs }">
            <button type="button" class="icon-btn icon-btn--delete" @click="deleteVehicle(vehicle)" v-on="on" v-bind="attrs"><i class="far fa-trash-alt"></i></button>
           </template>
           <span>Delete</span>
          </v-tooltip>
         </div>
         <div v-else class="deleted-row">
          <span>Vehicle Deleted</span>
          <v-tooltip bottom>
           <template v-slot:activator="{ on, attrs }">
            <button type="button" class="icon-btn icon-btn--restore" @click="restoreVehicle(vehicle)" v-on="on" v-bind="attrs"><i class="fas fa-undo"></i></button>
           </template>
           <span>Restore</span>
          </v-tooltip>
         </div>
        </td>
       </tr>
      </tbody>
     </v-simple-table>

     <div class="list-footer">
      <span class="showing-text">Showing {{ showingFrom }} to {{ showingTo }} of {{ totalCount }} vehicles</span>
      <v-pagination v-model="page" :length="pageCount" :total-visible="7" class="pagination"></v-pagination>
      <div class="per-page">
       <span>Show</span>
       <v-select v-model="perPage" :items="perPageOptions" dense outlined hide-details class="per-page-select" @change="changePerPage"></v-select>
       <span>per page</span>
      </div>
     </div>
    </v-card>
   </div>

   <v-dialog v-model="dialog" v-if="dialog" :width="dialogWidth" persistent>
    <v-card class="card-container">
     <VehicleInsert v-if="dialogStatus === 'edit'" :vehicleDetails="vehicleDetails" :editStatus="true" @vehicle-updated="updateVehicle" @cancel-insert="closeDialog"></VehicleInsert>

     <VehicleInsert v-if="dialogStatus === 'insert'" :vehicleDetails="{}" :editStatus="false" @vehicle-updated="updateVehicle" @cancel-insert="closeDialog"></VehicleInsert>

     <DeactivationModal
      v-if="dialogStatus === 'statusChange'"
      :token="vehicleDetails.token"
      :type="2"
      @change-status="updateVehicle"
      @dialog-closed="closeDialog"
      :title="vehicleDetails.trash ? 'Activate' : 'Deactivate'"
      :subtitle="[vehicleDetails.vehicleNo, [vehicleDetails.manufacturer, vehicleDetails.model].filter(Boolean).join(' '), vehicleDetails.category].filter(Boolean).join(' · ')"
     ></DeactivationModal>

     <VehicleRemarksList
      v-if="dialogStatus === 'remarks'"
      :token="vehicleDetails.token"
      :subtitle="[vehicleDetails.vehicleNo, [vehicleDetails.manufacturer, vehicleDetails.model].filter(Boolean).join(' '), vehicleDetails.category].filter(Boolean).join(' · ')"
      @edit-dialog="closeDialog"
     ></VehicleRemarksList>

     <VehicleDocument @recallapi="vehicleListing" v-if="dialogStatus === 'document'" :vehicleDetails="vehicleDetails" @cancel-insert="closeDialog"></VehicleDocument>
    </v-card>
   </v-dialog>
   <v-dialog v-model="deleteDialog" max-width="600px">
    <v-card>
     <v-card-title> Confirm Deletion </v-card-title>

     <v-card-text style="color: black; font-size: 16px; margin-top: 2px">
      Are you sure you want to delete the vehicle <strong style="margin-left: 2px">{{ vehicledata.vehicleNo }}</strong
      >?
     </v-card-text>
     <v-card-text>
      <v-textarea label="Remarks *" :rules="[(v) => !!v || 'Remarks are required']" v-model="deleteRemarks" outlined rows="3" placeholder="Enter remarks "></v-textarea>
     </v-card-text>
     <v-card-actions>
      <v-spacer></v-spacer>

      <v-btn outlined color="grey darken-1" text @click="deleteDialog = false"> Cancel </v-btn>

      <v-btn class="red darken-1" color="white" text @click="confirmDelete"> Delete </v-btn>
     </v-card-actions>
    </v-card>
   </v-dialog>
   <Confirm ref="confirm"></Confirm>
   <Loading v-if="loading"></Loading>
  </div>
 </section>
</template>

<script>
import Filters from "@/components/common/filter/Filters";
import axios from "@/axios/axios-global";
import moment from "moment";
import VehicleInsert from "../vehicle-insert/VehicleInsert.vue";
import DeactivationModal from "../../modal/DeactivationModal.vue";
import VehicleRemarksList from "../vehicle-remarks-list/VehicleRemarksList.vue";
import VehicleDocument from "../vehicle-document/VehicleDocument.vue";
import Loading from "@/components/common/Loading";
import Confirm from "../../common/Confirm.vue";

const VALIDITY_COLUMNS = [
 { key: "insurance", doc: "insurance", label: "Insurance Validity" },
 { key: "rc", doc: "rcbook", label: "RC Validity" },
 { key: "puc", doc: "pollution", label: "PUC Validity" },
 { key: "tax", doc: "tax", label: "Tax Validity" },
 { key: "permit", doc: "carriagepermit", label: "Permit Validity" },
 { key: "fitness", doc: "fitness", label: "Fitness Validity" },
];

export default {
 name: "VehicleDashboardNew",
 components: { Filters, VehicleInsert, DeactivationModal, VehicleRemarksList, VehicleDocument, Loading, Confirm },
 data() {
  return {
   page: 1,
   perPage: 10,
   perPageOptions: [10, 20, 50],
   sortKey: "",
   sortDesc: false,
   validityColumns: VALIDITY_COLUMNS,
   columns: [
    { key: "vehicle", label: "Vehicle", sortable: true },
    { key: "driver", label: "Drivers", sortable: true },
    ...VALIDITY_COLUMNS.map((c) => ({ key: c.key, label: c.label, sortable: true })),
    { key: "document", label: "Documents", sortable: false },
    { key: "action", label: "Actions", sortable: false },
   ],
   filters: "",

   vehicleList: [],
   vehicleDetails: {},
   totalCount: 0,
   dialog: false,
   dialogStatus: "",
   dialogWidth: "1000",
   active: 0,
   deactive: 0,
   loading: false,
   deleteDialog: false,
   deleteRemarks: "",
   vehicledata: "",
  };
 },
 computed: {
  pageCount() {
   return Math.max(1, Math.ceil(this.totalCount / this.perPage));
  },
  showingFrom() {
   return this.totalCount ? (this.page - 1) * this.perPage + 1 : 0;
  },
  showingTo() {
   return Math.min(this.page * this.perPage, this.totalCount);
  },
  // sorts the vehicles loaded for the current page
  sortedList() {
   if (!this.sortKey) return this.vehicleList;
   const direction = this.sortDesc ? -1 : 1;
   return [...this.vehicleList].sort((a, b) => {
    const x = this.sortValue(a, this.sortKey);
    const y = this.sortValue(b, this.sortKey);
    if (x === y) return 0;
    if (x === "") return 1; // empty values always last
    if (y === "") return -1;
    return x > y ? direction : -direction;
   });
  },
 },
 mounted() {
  this.getVehicleList(this.filters);
 },
 watch: {
  page() {
   this.getVehicleList(this.filters);
  },
 },
 methods: {
  vehicleListing() {
   this.getVehicleList(this.filters);
  },
  getFilters(data) {
   this.filters = data;
   this.getVehicleList(this.filters);
   this.page = 1;
  },
  changePerPage() {
   if (this.page === 1) this.getVehicleList(this.filters);
   else this.page = 1;
  },
  async getVehicleList(data) {
   try {
    this.loading = true;
    const response = await axios.post("vehicle/list", { search: data.searchValue ? data.searchValue.trim() : "", page: this.page, limit: this.perPage, is_pagination: true });
    this.loading = false;
    this.active = response.data.active;
    this.deactive = response.data.inactive;
    this.vehicleList = response.data.list;
    this.totalCount = response.data.total;
   } catch (error) {
    this.loading = false;
   } finally {
    this.loading = false;
   }
  },
  toggleSort(key) {
   if (this.sortKey === key) {
    if (this.sortDesc) {
     this.sortKey = "";
     this.sortDesc = false;
    } else {
     this.sortDesc = true;
    }
   } else {
    this.sortKey = key;
    this.sortDesc = false;
   }
  },
  sortIcon(key) {
   if (this.sortKey !== key) return "mdi-unfold-more-horizontal";
   return this.sortDesc ? "mdi-chevron-down" : "mdi-chevron-up";
  },
  sortValue(vehicle, key) {
   if (key === "vehicle") return (vehicle.vehicleNo || "").toLowerCase();
   if (key === "driver") return this.driverNames(vehicle).toLowerCase();
   const column = VALIDITY_COLUMNS.find((c) => c.key === key);
   const doc = column && vehicle.document ? vehicle.document[column.doc] : null;
   return doc && doc.expdate ? String(doc.expdate) : "";
  },
  driverNames(vehicle) {
   return (vehicle.drivers || []).map((d) => d.name).join(", ");
  },
  categoryClass(category) {
   const name = (category || "").toLowerCase();
   if (["sedan", "hatchback"].includes(name)) return "category-badge--blue";
   if (name === "suv") return "category-badge--purple";
   if (name === "muv") return "category-badge--green";
   return "category-badge--grey";
  },
  updateVehicle() {
   this.dialog = false;
   this.getVehicleList(this.filters);
  },
  closeDialog() {
   this.dialog = false;
  },
  openDialog(data, status) {
   this.dialog = true;
   this.vehicleDetails = data;
   this.dialogStatus = status;
   if (status === "document") this.dialogWidth = "1200";
   else if (status === "statusChange") this.dialogWidth = "580";
   else if (status === "vehicleAdd") this.dialogWidth = "500";
   else this.dialogWidth = "1100";
  },
  getExpiry(date) {
   date = date ? moment(date, "YYYYMMDD").format("DD-MM-YYYY") : "";
   date = date && date != "Invalid date" ? date : "---";
   return date;
  },
  checkExpiry(data) {
   return [3, 4].includes(data) ? true : false;
  },
  deleteVehicle(data) {
   this.deleteDialog = true;
   this.vehicledata = data;
  },
  async confirmDelete() {
   try {
    if (!this.deleteRemarks.trim()) {
     this.$toasted.show("Please Enter Remarks  for deletion", {
      position: "top-center",
      duration: 4000,
      type: "error",
     });
     return;
    }

    const payload = {
     remark: this.deleteRemarks,
     token: this.vehicledata.token,
     trash: 1,
    };
    const response = await axios.post("vehicle/moveToTrash", payload);
    if (response.status === 200) {
     this.deleteDialog = false;
     this.deleteRemarks = "";
     this.getVehicleList({ searchValue: "", initial: false, page: this.page });
     this.$toasted.show("Vehicle Deleted Successfully", {
      position: "bottom-center",
      duration: 4000,
      type: "success",
     });
    } else {
     this.$toasted.show("An error occurred while deleting the vehicle", {
      position: "bottom-center",
      duration: 4000,
      type: "error",
     });
    }
   } catch (error) {
    console.error("Error deleting vehicle:", error);
    this.$toasted.show("An error occurred while deleting the vehicle", {
     position: "bottom-center",
     duration: 4000,
     type: "error",
    });
   }
  },
  async restoreVehicle(data) {
     const confirmTitle = "Confirm Restore";
     const confirmText = `Are you sure you want to restore the vehicle "${data.vehicleNo}"?`;
     const confirmIcon = "fas fa-undo";
     const confirmed = await this.$refs.confirm.open(confirmTitle, confirmText, {
       color: "green",
       icon: confirmIcon,
     });
     if (confirmed) {
       try {
         const payload = {
           token: data.token,
           trash: 0,
         }
         const response = await axios.post("vehicle/restore", payload);
         this.$toasted.show(response.data, {
           position: "bottom-center",
           duration: 4000,
           type: response.status === 200 ? "success" : "error",
         });

       } catch (error) {
         this.$root.$emit("show_snackbar", { text: error, color: "#FF0000" });
         console.error(error);
       } finally {
        this.getVehicleList({ searchValue: "", initial: false, page: this.page });
       }
     }
   },
 },
};
</script>

<style lang="scss" scoped>
$border: #e5e9f0;
$text: #1f2937;
$muted: #6b7280;
$header-bg: #f5f7fa;

.vehicle-dashboard {
 .vehicle-dashboard-container {
  padding: 10px;

  .filter-block {
   padding: 20px;
   background: #fff;
   border-radius: 8px;
   box-shadow: 0 1px 3px rgba(16, 24, 40, 0.08);

   // outlined search box with a search icon (Filters is shared, so styled here only)
   ::v-deep .filter-search-blk {
    .v-input {
     margin-top: 0;
     padding-top: 0;
    }
    .v-input__slot {
     min-height: 44px;
     padding: 0 12px 0 40px !important;
     border: 1px solid #cfd6e1;
     border-radius: 6px;
     background: #fff;
     position: relative;

     &::before,
     &::after {
      display: none;
     }
    }
    .v-input__slot .v-text-field__slot::before {
     content: "\f002";
     font-family: "Font Awesome 5 Free";
     font-weight: 900;
     position: absolute;
     left: -26px;
     top: 50%;
     transform: translateY(-50%);
     color: #4b5563;
     font-size: 15px;
    }
    input {
     font-size: 14px;
    }
   }
  }

  .list-block {
   margin-top: 20px;
   padding: 20px;
   border-radius: 8px;
   box-shadow: 0 1px 3px rgba(16, 24, 40, 0.08);
  }

  .list-header {
   display: flex;
   align-items: center;
   justify-content: space-between;
   margin-bottom: 16px;

   .add-btn {
    color: #fff;
    font-weight: 700;
    letter-spacing: 0.04em;
    border-radius: 6px;

    .fas {
     margin-left: 8px;
    }
   }
  }

  .count-block {
   display: flex;
   align-items: center;
   gap: 12px;

   .count-chip {
    padding: 6px 16px;
    border-radius: 20px;
    font-size: 14px;

    b {
     margin-left: 6px;
     color: $text;
    }
   }
   .count-chip--total {
    background: #e8f0fe;
    color: #374151;
   }
   .count-chip--active {
    background: #e6f4ea;
    color: #1e7b34;
   }
   .count-chip--inactive {
    background: #fde8e8;
    color: #c62828;
   }
  }

  .vehicle-table {
   border: 1px solid $border;
   border-radius: 8px;
   overflow: hidden;

   ::v-deep table {
    border-collapse: collapse;
   }

   th {
    background: $header-bg !important;
    color: $text !important;
    font-size: 13px !important;
    font-weight: 600;
    white-space: normal;
    border-right: 1px solid $border;
    border-bottom: 1px solid $border !important;
    padding: 10px 14px !important;

    &:last-child {
     border-right: none;
    }

    &.sortable {
     cursor: pointer;
     user-select: none;
    }

    .th-content {
     display: flex;
     align-items: center;
     justify-content: space-between;
     gap: 6px;
    }

    .sort-icon {
     color: #9ca3af;

     &.active {
      color: #1976d2;
     }
    }
   }

   td {
    font-size: 14px;
    color: $text;
    padding: 10px 14px !important;
    border-right: 1px solid $border;
    border-bottom: 1px solid $border !important;

    &:last-child {
     border-right: none;
    }
   }

   tbody tr:nth-child(even) {
    background: #fafbfc;
   }

   tbody tr:hover {
    background: #f3f6fb !important;
   }

   .empty-row {
    text-align: center;
    color: $muted;
    padding: 30px !important;
   }

   tr.deleted {
    background: #fff1f1 !important;
   }
  }

  .vehicle-details {
   display: flex;
   align-items: center;
   gap: 12px;

   ::v-deep .active-status {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    background: #1e9e3e;
    border-radius: 50%;
   }
   .inactive {
    background: #d32f2f;
   }

   .details {
    display: grid;
    gap: 4px;

    .vehicle-no {
     font-weight: 700;
     font-size: 15px;
    }

    .model-line {
     display: flex;
     align-items: center;
     gap: 10px;
    }

    .model-name {
     color: #4b5563;
     font-size: 14px;
    }
   }
  }

  .category-badge {
   padding: 2px 10px;
   font-size: 13px;
   font-weight: 600;
   border-radius: 6px;
   white-space: nowrap;
  }
  .category-badge--blue {
   color: #1a5fd0;
   background: #e8f0fe;
  }
  .category-badge--purple {
   color: #6d28d9;
   background: #efe9fd;
  }
  .category-badge--green {
   color: #1e7b34;
   background: #e6f4ea;
  }
  .category-badge--grey {
   color: #4b5563;
   background: #eef0f3;
  }

  .driver-column {
   max-width: 140px;

   .driver-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
   }
  }

  .document-column {
   white-space: nowrap;
   text-align: center;

   .fas {
    color: rgb(218, 3, 3);
    font-size: 10px;
   }
   .expiry-soon {
    color: #ffbe01;
   }
  }

  .documents-btn {
   display: inline-flex;
   align-items: center;
   gap: 8px;
   padding: 8px 16px;
   font-size: 13px;
   font-weight: 700;
   color: #1565a8;
   background: #e3f2fb;
   border-radius: 6px;
   text-transform: uppercase;
   white-space: nowrap;
   transition: background-color 0.15s;

   .far {
    font-size: 15px;
   }

   &:hover {
    background: #cfe8f7;
   }
  }

  .action-column {
   white-space: nowrap;

   .action-buttons,
   .deleted-row {
    display: flex;
    align-items: center;
    gap: 10px;
   }

   .deleted-row span {
    color: #c62828;
    font-weight: 600;
    font-size: 13px;
   }
  }

  .icon-btn {
   width: 40px;
   height: 40px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   border-radius: 8px;
   font-size: 15px;
   transition: filter 0.15s;

   &:hover {
    filter: brightness(0.95);
   }
  }
  .icon-btn--edit {
   color: #1976d2;
   background: #e8f0fe;
  }
  .icon-btn--status {
   color: #b45309;
   background: #fff4e0;
  }
  .icon-btn--info {
   color: #374151;
   background: #eef0f3;
  }
  .icon-btn--delete {
   color: #d32f2f;
   background: #fde8e8;
  }
  .icon-btn--restore {
   color: #1e7b34;
   background: #e6f4ea;
  }

  .list-footer {
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 16px;
   margin-top: 16px;

   .showing-text {
    color: #4b5563;
    font-size: 14px;
    white-space: nowrap;
   }

   .pagination {
    flex: 1;
   }

   ::v-deep .v-pagination__item,
   ::v-deep .v-pagination__navigation {
    box-shadow: none;
    border: 1px solid #d1d5db;
    border-radius: 6px;
   }

   ::v-deep .v-pagination__item--active {
    border-color: #1976d2;
   }

   .per-page {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #4b5563;
    font-size: 14px;
    white-space: nowrap;

    .per-page-select {
     width: 90px;
    }
   }
  }
 }
}
</style>
